import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { GitMerge, ArrowRightLeft, Search, ShieldCheck, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { SuperAdminNav } from "@/pages/SuperAdminInvitationsPage";

/**
 * Slice 3 — SA Superpowers
 *
 *   Tab 1 — Merge Duplicate Users: search two users, dry-run preview the
 *           merge, resolve per-club member-number collisions, confirm with
 *           typed phrase, commit. Source is soft-archived into users_archive.
 *
 *   Tab 2 — Transfer Membership Across Clubs: search a user, pick the from-
 *           club and to-club, optionally override member_number, commit.
 *           Historical orders/wishlist stay at the source club (Q1=a).
 */
const TABS = [
  { key: "merge", label: "Merge Duplicate Users", Icon: GitMerge },
  { key: "transfer", label: "Transfer Membership", Icon: ArrowRightLeft },
];

export default function SuperAdminMergePage() {
  const [tab, setTab] = useState("merge");
  return (
    <div className="bg-cream min-h-screen" data-testid="sa-merge-page">
      <header className="bg-[#0f1a14] text-[#F9F8F4] border-b border-[#1A422D]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <GitMerge size={22} className="text-[#D9C68A]" />
            <div>
              <div className="overline" style={{ color: "#D9C68A" }}>Platform</div>
              <div className="font-serif text-2xl mt-0.5">Super Admin Superpowers</div>
            </div>
          </div>
          <Link
            to="/super-admin"
            data-testid="sa-merge-back"
            className="inline-flex items-center gap-2 border border-[#D9C68A]/50 text-[#D9C68A] px-3 py-1.5 text-xs uppercase tracking-[0.22em] hover:bg-[#1A422D] transition-colors"
          >
            <ShieldCheck size={14} /> Back to Console
          </Link>
        </div>
      </header>
      <SuperAdminNav active="merge" />

      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10 space-y-8">
        <div className="flex items-center gap-2 flex-wrap">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              data-testid={`sa-merge-tab-${key}`}
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${
                tab === key
                  ? "bg-[#1A422D] text-[#F9F8F4] border-[#1A422D]"
                  : "text-[#1A422D] border-[#1A422D]/30 hover:bg-[#1A422D]/5"
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
        {tab === "merge" ? <MergeTab /> : <TransferTab />}
      </div>
    </div>
  );
}

/* -------- Shared search field -------- */
function UserPicker({ label, value, onPick, testId }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (q.trim().length < 2) return;
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/users/search", { params: { q: q.trim(), limit: 10 } });
      setResults(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="surface-card p-5 space-y-3">
      <div className="overline text-[10px]">{label}</div>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7A7A]" />
          <input
            data-testid={`${testId}-search`}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="email or name…"
            className="input-field w-full pl-9"
          />
        </div>
        <button onClick={search} disabled={loading || q.trim().length < 2} data-testid={`${testId}-search-btn`} className="btn-secondary text-xs">
          {loading ? "…" : "Search"}
        </button>
      </div>

      {value && (
        <div data-testid={`${testId}-selected`} className="px-3 py-3 bg-[#FAF9F2] border border-[#1A422D]/30">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[#1A1A1A] font-medium">{value.name || "(no name)"}</div>
              <div className="text-xs text-[#7A7A7A]">{value.email}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#1A422D] mt-1">{value.role}</div>
            </div>
            <button onClick={() => onPick(null)} className="text-xs text-[#A33] underline">Clear</button>
          </div>
          {value.footprint && (
            <div className="mt-2 flex gap-3 text-[11px] text-[#4A4A4A] flex-wrap">
              <span>orders: <strong>{value.footprint.orders}</strong></span>
              <span>wishlist: <strong>{value.footprint.wishlist}</strong></span>
              <span>audit: <strong>{value.footprint.audit_actor}</strong></span>
            </div>
          )}
        </div>
      )}

      {results.length > 0 && !value && (
        <div data-testid={`${testId}-results`} className="border border-[#E5E3D8] divide-y divide-[#E5E3D8]">
          {results.map((u) => (
            <button
              key={u.user_id}
              data-testid={`${testId}-result-${u.user_id}`}
              onClick={() => { onPick(u); setResults([]); }}
              className="w-full px-3 py-2.5 text-left hover:bg-[#FAF9F2] flex items-start justify-between gap-3"
            >
              <div>
                <div className="text-[#1A1A1A]">{u.name || "(no name)"}</div>
                <div className="text-xs text-[#7A7A7A]">{u.email}</div>
              </div>
              <div className="text-[10px] text-[#7A7A7A]">
                {u.footprint?.orders || 0} orders · {(u.clubs?.length || 0) + (u.club_id && !(u.clubs || []).some(c => c.club_id === u.club_id) ? 1 : 0)} clubs
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------- Tab 1: Merge -------- */
function MergeTab() {
  const [source, setSource] = useState(null);
  const [target, setTarget] = useState(null);
  const [preview, setPreview] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [busy, setBusy] = useState(false);

  const runPreview = useCallback(async () => {
    if (!source || !target) return;
    setBusy(true);
    setPreview(null);
    try {
      const { data } = await api.post("/super-admin/users/merge-preview", {
        source_user_id: source.user_id, target_user_id: target.user_id,
      });
      setPreview(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  }, [source, target]);

  const commit = async () => {
    setBusy(true);
    try {
      const club_overrides = Object.entries(overrides).map(([club_id, v]) => ({
        club_id, prefer: v.prefer || "target",
        new_member_number: v.new_member_number || null,
      })).filter((o) => o.prefer !== "target" || o.new_member_number);
      const { data } = await api.post("/super-admin/users/merge", {
        source_user_id: source.user_id, target_user_id: target.user_id,
        club_overrides, confirm: confirmPhrase,
      });
      toast.success(`Merged. ${data.fk_counts.orders} orders + ${data.fk_counts.wishlist} wishlist items re-pointed.`);
      setSource(null); setTarget(null); setPreview(null); setOverrides({}); setConfirmPhrase("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="overline">Merge Duplicate Users</div>
        <h2 className="font-serif text-3xl text-[#1A1A1A] mt-1">Collapse two accounts into one</h2>
        <p className="text-sm text-[#4A4A4A] mt-2 max-w-2xl">
          Pick a <strong>source</strong> (will be soft-archived) and a <strong>target</strong> (will absorb the
          source&rsquo;s memberships, orders, wishlist, and invitation history). Audit logs preserve the original
          author but gain an <code>actor_merged_into</code> tag.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <UserPicker label="Source · will be archived" value={source} onPick={setSource} testId="sa-merge-source" />
        <UserPicker label="Target · will absorb the source" value={target} onPick={setTarget} testId="sa-merge-target" />
      </div>

      <div className="flex items-center gap-3">
        <button
          data-testid="sa-merge-preview-btn"
          onClick={runPreview}
          disabled={!source || !target || busy || source?.user_id === target?.user_id}
          className="btn-primary"
        >
          {busy ? "Calculating…" : "Preview merge"}
        </button>
        {source?.user_id === target?.user_id && source && (
          <span className="text-xs text-[#A33]">Source and target must be different.</span>
        )}
      </div>

      {preview && (
        <section data-testid="sa-merge-preview" className="surface-card p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
            <Stat label="Clubs after merge" value={preview.summary.total_clubs_after_merge} />
            <Stat label="Orders to re-point" value={preview.summary.orders_to_repoint} />
            <Stat label="Wishlist to re-point" value={preview.summary.wishlist_to_repoint} />
          </div>

          {preview.collisions.length > 0 && (
            <div data-testid="sa-merge-collisions" className="space-y-3">
              <div className="overline text-[10px]">Per-club collisions — choose the surviving member number</div>
              {preview.collisions.map((c) => {
                const ov = overrides[c.club_id] || { prefer: "target", new_member_number: "" };
                return (
                  <div key={c.club_id} data-testid={`sa-merge-collision-${c.club_id}`} className="px-4 py-3 border border-[#B59A5A] bg-[#F8F1DE]">
                    <div className="text-xs text-[#8a6f33] font-mono mb-2">{c.club_id}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                      <label className={`px-3 py-2 border cursor-pointer ${ov.prefer === "target" ? "border-[#1A422D] bg-[#1A422D]/10 text-[#1A422D]" : "border-[#E5E3D8]"}`}>
                        <input
                          type="radio"
                          name={`prefer-${c.club_id}`}
                          checked={ov.prefer === "target"}
                          onChange={() => setOverrides({ ...overrides, [c.club_id]: { ...ov, prefer: "target" } })}
                          className="mr-2"
                          data-testid={`sa-merge-prefer-target-${c.club_id}`}
                        />
                        Keep target&rsquo;s #{c.target_member_number || "—"}
                      </label>
                      <label className={`px-3 py-2 border cursor-pointer ${ov.prefer === "source" ? "border-[#1A422D] bg-[#1A422D]/10 text-[#1A422D]" : "border-[#E5E3D8]"}`}>
                        <input
                          type="radio"
                          name={`prefer-${c.club_id}`}
                          checked={ov.prefer === "source"}
                          onChange={() => setOverrides({ ...overrides, [c.club_id]: { ...ov, prefer: "source" } })}
                          className="mr-2"
                          data-testid={`sa-merge-prefer-source-${c.club_id}`}
                        />
                        Use source&rsquo;s #{c.source_member_number || "—"}
                      </label>
                      <input
                        type="text"
                        placeholder="Or type a new #"
                        value={ov.new_member_number}
                        onChange={(e) => setOverrides({ ...overrides, [c.club_id]: { ...ov, new_member_number: e.target.value } })}
                        data-testid={`sa-merge-override-${c.club_id}`}
                        className="input-field text-sm"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="border-t border-[#E5E3D8] pt-4 space-y-3">
            <div className="flex items-start gap-3 text-sm text-[#8a1a1a] bg-[#fbe9e9] border border-[#A33]/40 px-3 py-2">
              <AlertTriangle size={16} className="mt-0.5" />
              <div>
                This action soft-archives <strong>{preview.source.email}</strong> and is logged to the audit trail.
                Type <code>CONFIRM-MERGE</code> below to enable the commit button.
              </div>
            </div>
            <input
              type="text"
              value={confirmPhrase}
              onChange={(e) => setConfirmPhrase(e.target.value)}
              data-testid="sa-merge-confirm-input"
              placeholder="Type CONFIRM-MERGE"
              className="input-field text-sm"
            />
            <button
              data-testid="sa-merge-commit"
              onClick={commit}
              disabled={busy || confirmPhrase !== "CONFIRM-MERGE"}
              className="btn-primary"
            >
              {busy ? "Merging…" : "Commit merge"}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

/* -------- Tab 2: Transfer Membership -------- */
function TransferTab() {
  const [user, setUser] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [fromClubId, setFromClubId] = useState("");
  const [toClubId, setToClubId] = useState("");
  const [preserveNumber, setPreserveNumber] = useState(true);
  const [newNumber, setNewNumber] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    api.get("/super-admin/clubs").then((r) => setClubs((r.data || []).filter(c => c.status === "active"))).catch(() => {});
  }, []);

  const userClubs = React.useMemo(() => {
    if (!user) return [];
    const arr = [...(user.clubs || [])];
    if (user.club_id && !arr.some(c => c.club_id === user.club_id)) {
      const matching = clubs.find(c => c.club_id === user.club_id);
      if (matching) arr.push({ club_id: matching.club_id, name: matching.name, status: "active", legacy_implicit: true });
    }
    return arr;
  }, [user, clubs]);

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/super-admin/users/${user.user_id}/transfer-membership`, {
        from_club_id: fromClubId, to_club_id: toClubId,
        preserve_member_number: preserveNumber,
        new_member_number: newNumber.trim() || null,
      });
      toast.success(`Transferred ${user.email} → ${clubs.find(c => c.club_id === toClubId)?.name}.${data.to_member_number ? " #" + data.to_member_number : ""}`);
      setUser(null); setFromClubId(""); setToClubId(""); setNewNumber("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="overline">Transfer Membership</div>
        <h2 className="font-serif text-3xl text-[#1A1A1A] mt-1">Move a member to another club</h2>
        <p className="text-sm text-[#4A4A4A] mt-2 max-w-2xl">
          Past orders and wishlist items stay at the original club. The member&rsquo;s session pointer
          ({" "}<code>users.club_id</code>) is updated so their next page load lands in the new tenant.
        </p>
      </div>
      <UserPicker label="Member" value={user} onPick={setUser} testId="sa-transfer-user" />

      {user && (
        <section className="surface-card p-6 space-y-4" data-testid="sa-transfer-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="overline text-[10px] block mb-1.5">From (source club)</label>
              <select
                data-testid="sa-transfer-from"
                value={fromClubId}
                onChange={(e) => setFromClubId(e.target.value)}
                className="input-field w-full"
              >
                <option value="">Choose…</option>
                {userClubs.map((c) => {
                  const club = clubs.find(cl => cl.club_id === c.club_id);
                  return (
                    <option key={c.club_id} value={c.club_id}>
                      {club?.name || c.name || c.club_id} {c.member_number ? `· #${c.member_number}` : ""}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="overline text-[10px] block mb-1.5">To (destination club)</label>
              <select
                data-testid="sa-transfer-to"
                value={toClubId}
                onChange={(e) => setToClubId(e.target.value)}
                className="input-field w-full"
              >
                <option value="">Choose…</option>
                {clubs.filter(c => c.club_id !== fromClubId).map((c) => (
                  <option key={c.club_id} value={c.club_id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 text-sm text-[#4A4A4A]">
              <input
                type="checkbox"
                checked={preserveNumber}
                onChange={(e) => setPreserveNumber(e.target.checked)}
                data-testid="sa-transfer-preserve-number"
              />
              Keep member number from source
            </label>
            <input
              type="text"
              placeholder="Or set a new member # (optional)"
              value={newNumber}
              onChange={(e) => setNewNumber(e.target.value)}
              data-testid="sa-transfer-new-number"
              className="input-field text-sm flex-1 min-w-[200px]"
            />
          </div>

          <button
            data-testid="sa-transfer-commit"
            onClick={commit}
            disabled={busy || !fromClubId || !toClubId || fromClubId === toClubId}
            className="btn-primary"
          >
            {busy ? "Transferring…" : "Transfer membership"}
          </button>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="px-3 py-3 border border-[#E5E3D8] bg-[#FAF9F2]">
      <div className="overline text-[10px]">{label}</div>
      <div className="font-serif text-2xl text-[#1A1A1A] mt-0.5">{value || 0}</div>
    </div>
  );
}

/* exports for the SA nav strip — referenced by SuperAdminInvitationsPage */
export { UserPicker, Stat };
